import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BionicText } from '../components/custom/BionicText';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Salad, 
  ShoppingCart, 
  Calendar, 
  Sparkles, 
  ArrowRight,
  Check,
  Store,
  Heart
} from 'lucide-react';

const heroImages = {
  main: "https://images.unsplash.com/photo-1568584182785-4b4067773447?w=1200&q=85",
  garden: "https://images.unsplash.com/photo-1466637574441-749b8f19452f?w=1920&q=80",
  farm: "https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?w=1920&q=80",
  herbs: "https://images.unsplash.com/photo-1515586838455-8f8e37eb37db?w=1920&q=80",
  mushrooms: "https://images.pexels.com/photos/14612416/pexels-photo-14612416.jpeg?w=800&q=85",
  lionsMane: "https://images.unsplash.com/photo-1767606924632-81355485785f?w=800&q=85",
};

const features = [
  {
    icon: Calendar,
    title: "AI-Powered Meal Planning",
    description: "Get personalized weekly meal plans with complete macro and micronutrient tracking."
  },
  {
    icon: ShoppingCart,
    title: "Smart Grocery Lists",
    description: "Automatically generated shopping lists with price comparisons across local stores."
  },
  {
    icon: Store,
    title: "Store Price Comparison",
    description: "Compare prices from Trader Joe's, Whole Foods, and Sprouts to save money."
  },
  {
    icon: Sparkles,
    title: "Professional Recipe Cards",
    description: "Download beautifully designed PDF recipe cards with nutritional info."
  }
];

const values = [
  { color: "bg-red-500", label: "Life" },
  { color: "bg-orange-500", label: "Healing" },
  { color: "bg-yellow-500", label: "Sunlight" },
  { color: "bg-green-500", label: "Nature" },
  { color: "bg-blue-500", label: "Harmony" },
  { color: "bg-purple-500", label: "Spirit" },
];

const dietTypes = [
  "Vegetarian",
  "Keto",
  "Gluten-Free",
  "Anti-Histamine",
  "Grass-Fed",
  "Hormone-Free"
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/auth');
    }
  };

  return (
    <main className="min-h-screen">
      {/* Hero Section with Selective Color */}
      <section 
        className="relative pt-32 pb-20 px-4 overflow-hidden"
        aria-labelledby="hero-heading"
      >
        {/* B&W Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{ 
            backgroundImage: `url(${heroImages.garden})`,
            filter: 'grayscale(100%) contrast(1.1) brightness(0.9)'
          }}
          aria-hidden="true" 
        />
        {/* Overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-background/85 to-background/90" aria-hidden="true" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 opacity-0 animate-fade-in">
              <Badge variant="secondary" className="text-sm px-4 py-1.5">
                <Salad className="h-4 w-4 mr-2" aria-hidden="true" />
                Gastro-Thrive Nutrition
              </Badge>
              
              <h1 
                id="hero-heading"
                className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-foreground"
              >
                <BionicText>Thrive Through</BionicText>
                <span className="text-primary block mt-2">
                  <BionicText>Nourishment</BionicText>
                </span>
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed">
                <BionicText>
                  Build personalized diet plans as a lifestyle, not a fad. AI-powered meal planning with complete nutrition tracking and smart grocery lists.
                </BionicText>
              </p>

              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  className="rounded-full px-8 gap-2 shadow-lg hover:shadow-xl"
                  onClick={handleGetStarted}
                  data-testid="hero-cta-btn"
                >
                  Get Started Free
                  <ArrowRight className="h-5 w-5" aria-hidden="true" />
                </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="rounded-full px-8"
                  onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                  data-testid="learn-more-btn"
                >
                  Learn More
                </Button>
              </div>

              {/* Diet Types */}
              <div className="flex flex-wrap gap-2 pt-4">
                {dietTypes.map((diet) => (
                  <Badge 
                    key={diet} 
                    variant="outline" 
                    className="text-xs px-3 py-1"
                  >
                    {diet}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Hero Image - Vibrant mushrooms on grayscale background */}
            <div className="relative opacity-0 animate-fade-in stagger-2">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl hero-selective-color">
                {/* Grayscale background layer */}
                <div 
                  className="absolute inset-0 bg-cover bg-center"
                  style={{
                    backgroundImage: `url(${heroImages.herbs})`,
                    filter: 'grayscale(100%) contrast(1.2)'
                  }}
                  aria-hidden="true"
                />
                {/* Vibrant mushrooms overlay */}
                <img
                  src={heroImages.mushrooms}
                  alt="Fresh low-histamine mushrooms - cremini and button varieties known to reduce histamine"
                  className="relative w-full h-[500px] object-cover vibrant-food"
                  style={{ mixBlendMode: 'normal' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" aria-hidden="true" />
              </div>
              
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-card rounded-2xl p-4 shadow-xl border border-border max-w-xs opacity-0 animate-slide-in stagger-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-primary/10">
                    <Check className="h-5 w-5 text-primary" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="font-medium text-card-foreground">Anti-Histamine Friendly</p>
                    <p className="text-sm text-muted-foreground">Low-histamine ingredients</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section with Farm Background */}
      <section 
        id="features" 
        className="relative py-24 px-4"
        aria-labelledby="features-heading"
      >
        {/* B&W Farm Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{ 
            backgroundImage: `url(${heroImages.farm})`,
            filter: 'grayscale(100%) contrast(1.05) brightness(0.95)'
          }}
          aria-hidden="true" 
        />
        <div className="absolute inset-0 bg-muted/90 dark:bg-background/95" aria-hidden="true" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4">Features</Badge>
            <h2 
              id="features-heading"
              className="font-heading text-4xl md:text-5xl font-bold text-foreground"
            >
              <BionicText>Lifestyle Nutrition, Not Fad Diets</BionicText>
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              <BionicText>
                From personalized meal planning to smart shopping — Gastro-Thrive helps you thrive.
              </BionicText>
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="bg-card rounded-2xl p-6 border border-border hover:shadow-lg transition-shadow opacity-0 animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`feature-card-${index}`}
                >
                  <div className="p-3 rounded-xl bg-primary/10 w-fit mb-4">
                    <Icon className="h-6 w-6 text-primary" aria-hidden="true" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-card-foreground mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground">
                    <BionicText>{feature.description}</BionicText>
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Diversity & Inclusion Section */}
      <section 
        className="relative py-16 px-4 overflow-hidden" 
        aria-labelledby="diversity-heading"
      >
        {/* B&W Herb Garden Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${heroImages.herbs})`,
            filter: 'grayscale(100%) contrast(1.1) brightness(0.8)'
          }}
          aria-hidden="true" 
        />
        {/* Rainbow gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 via-yellow-500/10 via-green-500/10 via-blue-500/10 to-purple-500/10" aria-hidden="true" />
        <div className="absolute inset-0 bg-background/85 dark:bg-background/90" aria-hidden="true" />
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="flex justify-center gap-1 mb-6">
            {values.map((v, i) => (
              <div key={i} className={`w-8 h-2 ${v.color} rounded-full`} title={v.label} />
            ))}
          </div>
          <h2 
            id="diversity-heading"
            className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4"
          >
            <BionicText>Thriving Together, Nourishing All</BionicText>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-6">
            <BionicText>
              Gastro-Thrive believes healthy eating is for everyone. Our platform is built with equity, diversity, 
              and inclusion at its core — celebrating LGBTQIA+ communities and all people who deserve 
              access to nutritious, personalized meal planning.
            </BionicText>
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Badge variant="outline" className="px-4 py-2 border-red-400 text-foreground">Inclusive</Badge>
            <Badge variant="outline" className="px-4 py-2 border-orange-400 text-foreground">Accessible</Badge>
            <Badge variant="outline" className="px-4 py-2 border-yellow-400 text-foreground">Affirming</Badge>
            <Badge variant="outline" className="px-4 py-2 border-green-400 text-foreground">Welcoming</Badge>
            <Badge variant="outline" className="px-4 py-2 border-blue-400 text-foreground">Equitable</Badge>
            <Badge variant="outline" className="px-4 py-2 border-purple-400 text-foreground">Celebratory</Badge>
          </div>
        </div>
      </section>

      {/* Stores Section */}
      <section 
        className="relative py-24 px-4" 
        aria-labelledby="stores-heading"
      >
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h2 
            id="stores-heading"
            className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4"
          >
            <BionicText>Compare Prices From Your Favorite Stores</BionicText>
          </h2>
          <p className="text-muted-foreground mb-12 max-w-xl mx-auto">
            <BionicText>
              Find the best deals on fresh, healthy ingredients from local grocery stores.
            </BionicText>
          </p>
          
          <div className="flex flex-wrap justify-center gap-8 items-center opacity-60">
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/b5/Trader_Joe%27s_Logo.svg/2560px-Trader_Joe%27s_Logo.svg.png" 
              alt="Trader Joe's logo"
              className="h-12 object-contain"
            />
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a2/Whole_Foods_Market_2016_logo.svg/2560px-Whole_Foods_Market_2016_logo.svg.png" 
              alt="Whole Foods logo"
              className="h-12 object-contain"
            />
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/0/0e/Sprouts_Farmers_Market_logo.svg/2560px-Sprouts_Farmers_Market_logo.svg.png" 
              alt="Sprouts logo"
              className="h-12 object-contain"
            />
          </div>
        </div>
      </section>

      {/* CTA Section with Vibrant Food */}
      <section 
        className="relative py-24 px-4 overflow-hidden" 
        aria-labelledby="cta-heading"
      >
        {/* B&W Garden Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{ 
            backgroundImage: `url(${heroImages.garden})`,
            filter: 'grayscale(100%) contrast(1.1)'
          }}
          aria-hidden="true" 
        />
        <div className="absolute inset-0 bg-primary/10 dark:bg-primary/5" aria-hidden="true" />
        <div className="absolute inset-0 bg-background/90" aria-hidden="true" />
        
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2 
            id="cta-heading"
            className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-6"
          >
            <BionicText>Ready to Thrive?</BionicText>
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            <BionicText>
              Join the Gastro-Thrive community and transform your relationship with food — one meal at a time.
            </BionicText>
          </p>
          <Button 
            size="lg" 
            className="rounded-full px-10 gap-2 shadow-lg"
            onClick={handleGetStarted}
            data-testid="cta-btn"
          >
            Start Thriving Today
            <ArrowRight className="h-5 w-5" aria-hidden="true" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-border" role="contentinfo">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <Salad className="h-5 w-5 text-primary" aria-hidden="true" />
              <span className="font-heading font-semibold text-foreground">Gastro-Thrive</span>
            </div>
            
            {/* Pride banner in footer */}
            <div className="flex items-center gap-2">
              <div className="flex gap-0.5">
                {values.map((v, i) => (
                  <div key={i} className={`w-4 h-1 ${v.color} rounded-sm`} aria-hidden="true" />
                ))}
              </div>
              <span className="text-sm text-muted-foreground">Proudly inclusive</span>
            </div>
            
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Gastro-Thrive. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </main>
  );
}
